import { useMemo } from "react";
import { ChatBugReportFormChrome, type ChatBugReportVariant } from "@sudowrite/shared-chat";
import { getAirTableSupportUrl } from "src/components/HelpMenu";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { usePersistentChat } from "./PersistentChatContext";

export type BugReportType = ChatBugReportVariant;

export type ChatBugReportFormProps = {
  chatId: string;
  messageId: string;
  toolCallId: string;
  projectId: string;
  invocationId?: string | null;
  agentIssueSummary: string;
  diagnosis: string;
  possibleSolutions: string[];
  severity: "low" | "medium" | "high" | "critical";
  reportType?: BugReportType;
  submissionStatus?: "success" | "error" | null;
};

function activityIdFromMetadata(metadata: Record<string, unknown> | null | undefined): string | null {
  if (!metadata) return null;
  const invocationId = typeof metadata.invocation_id === "string" ? metadata.invocation_id.trim() : "";
  if (invocationId) return invocationId;
  const raindropEventId = typeof metadata.raindropEventId === "string" ? metadata.raindropEventId.trim() : "";
  return raindropEventId || null;
}

/** Activity ID for this form's assistant turn (not the chat's latest turn). */
function getActivityIdForMessage(
  messages: Array<{ id?: string; role?: string; metadata?: Record<string, unknown> | null }> | undefined,
  messageId: string,
): string | null {
  if (!messages?.length || !messageId) return null;

  const owningMessage = messages.find((message) => message?.id === messageId);
  const fromOwning = activityIdFromMetadata(owningMessage?.metadata);
  if (fromOwning) return fromOwning;

  const owningIndex = messages.findIndex((message) => message?.id === messageId);
  if (owningIndex < 0) return null;
  for (let i = owningIndex - 1; i >= 0; i -= 1) {
    if (messages[i]?.role !== "user") continue;
    for (let j = i + 1; j < messages.length; j += 1) {
      if (messages[j]?.role !== "assistant") continue;
      return activityIdFromMetadata(messages[j]?.metadata);
    }
    return activityIdFromMetadata(messages[i]?.metadata);
  }

  return null;
}

/**
 * SW-10049: Inline Airtable "Report a Bug" link (replaces the previous in-chat submit form).
 * AI-generated issue summary is intentionally not shown or submitted.
 */
export function ChatBugReportForm({
  messageId,
  invocationId,
  reportType = "bug",
  submissionStatus = null,
}: ChatBugReportFormProps) {
  const subscription = useSudowriteStore((state) => state.subscription);
  const { messages } = usePersistentChat();

  const activityId = useMemo(() => {
    if (typeof invocationId === "string" && invocationId.trim()) {
      return invocationId.trim();
    }
    return getActivityIdForMessage(
      messages as Array<{ id?: string; role?: string; metadata?: Record<string, unknown> | null }>,
      messageId,
    );
  }, [invocationId, messageId, messages]);

  const openForm = () => {
    const formUrl = getAirTableSupportUrl(subscription, { activityId });
    window.open(formUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <ChatBugReportFormChrome
      variant={reportType}
      submissionStatus={submissionStatus}
      onOpenForm={openForm}
    />
  );
}
